/**
 * Umordnen der Zeilenliste per Ziehen — die Rechenlogik hinter der
 * Verzeichnisansicht.
 *
 * Getrennt von der Oberfläche, weil hier die Fälle stecken, die man ansehen
 * können muss: ein Block nimmt seine Positionen mit, eine Position darf in
 * einen anderen Block wandern, und beides muss die flache Liste
 * (`draft.line_rows`) so hinterlassen, dass die PDF-Engine dieselbe Gliederung
 * liest wie der Editor.
 */
import type { WebInvoiceLine } from "./invoices";
import { gruppiereZeilen, istBlock } from "./invoiceBlocks";

/** Ein Eintrag der Gliederung: entweder ein Blockkopf oder eine Position. */
export type Gliederungszeile = {
  line: WebInvoiceLine;
  index: number;
  istBlock: boolean;
  /** Bei Positionen: gehört sie zu einem Block? Für die Einrückung. */
  imBlock: boolean;
  /** Die ID des umschließenden Blocks — zum Ein-/Ausklappen im Index. */
  blockId: string | null;
};

/** Die flache Liste als Gliederung — dieselbe Reihenfolge, nur mit Tiefe. */
export function gliederung(lines: WebInvoiceLine[]): Gliederungszeile[] {
  const zeilen: Gliederungszeile[] = [];
  for (const gruppe of gruppiereZeilen(lines)) {
    if (gruppe.block) {
      zeilen.push({
        line: gruppe.block, index: gruppe.blockIndex, istBlock: true, imBlock: false, blockId: null,
      });
    }
    for (const { line, index } of gruppe.zeilen) {
      zeilen.push({
        line, index, istBlock: false, imBlock: !!gruppe.block,
        blockId: gruppe.block ? gruppe.block.id : null,
      });
    }
  }
  return zeilen;
}

/** Wie viele Zeilen ein Eintrag beim Ziehen mitnimmt (Block + seine Positionen). */
function spanne(lines: WebInvoiceLine[], index: number): number {
  if (!istBlock(lines[index])) return 1;
  let ende = index + 1;
  while (ende < lines.length && !istBlock(lines[ende])) ende += 1;
  return ende - index;
}

/**
 * Eine Zeile (oder einen Block samt Inhalt) an eine neue Stelle setzen.
 *
 * `zielIndex` ist die Stelle in der **ursprünglichen** Liste, VOR die
 * eingefügt wird; `lines.length` heißt „ans Ende". Ein Block, der in sich
 * selbst gezogen wird, bleibt liegen — sonst verschwänden seine Positionen in
 * einem Loch, das es nach dem Herausnehmen nicht mehr gibt.
 */
export function verschiebeZu(
  lines: WebInvoiceLine[],
  vonIndex: number,
  zielIndex: number,
): WebInvoiceLine[] {
  if (vonIndex < 0 || vonIndex >= lines.length) return lines;
  const laenge = spanne(lines, vonIndex);
  const bis = vonIndex + laenge;
  if (zielIndex > vonIndex && zielIndex < bis) return lines;
  if (zielIndex === vonIndex) return lines;

  const stueck = lines.slice(vonIndex, bis);
  const rest = [...lines.slice(0, vonIndex), ...lines.slice(bis)];
  // Nach dem Herausnehmen rutscht alles hinter der Entnahmestelle nach vorn.
  const ziel = zielIndex > vonIndex ? zielIndex - laenge : zielIndex;
  return [...rest.slice(0, ziel), ...stueck, ...rest.slice(ziel)];
}

/**
 * Darf an dieser Stelle abgelegt werden?
 *
 * Nur eine Frage ist wirklich verboten: einen Block in seinen eigenen Bereich
 * zu ziehen. Alles andere ist erlaubt — auch eine Position aus einem Block
 * heraus oder in einen anderen hinein; genau dafür gibt es die Ansicht.
 */
export function ablageErlaubt(
  lines: WebInvoiceLine[],
  vonIndex: number,
  zielIndex: number,
): boolean {
  if (vonIndex < 0) return false;
  const laenge = spanne(lines, vonIndex);
  if (zielIndex === vonIndex) return false;
  if (zielIndex > vonIndex && zielIndex < vonIndex + laenge) return false;
  return true;
}
